import React, { useState, useEffect } from 'react';
import { collection, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';

const ProductEnquiriesSection = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');

  const statusOptions = [
    { value: 'all', label: 'All Enquiries', color: 'gray' },
    { value: 'new', label: 'New', color: 'blue' },
    { value: 'contacted', label: 'Contacted', color: 'yellow' },
    { value: 'quoted', label: 'Quoted', color: 'purple' },
    { value: 'closed', label: 'Closed', color: 'green' }
  ];

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'productEnquiries'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const enquiriesData = [];
      
      querySnapshot.forEach((doc) => {
        enquiriesData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setEnquiries(enquiriesData);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      alert('Error loading enquiries');
    } finally {
      setLoading(false);
    }
  };

  const updateEnquiryStatus = async (enquiryId, newStatus) => {
    try {
      await updateDoc(doc(db, 'productEnquiries', enquiryId), {
        status: newStatus,
        updatedAt: new Date()
      });
      
      setEnquiries(enquiries.map(enquiry => 
        enquiry.id === enquiryId 
          ? { ...enquiry, status: newStatus }
          : enquiry
      ));
      
      alert('Status updated successfully!');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
    }
  };

  const deleteEnquiry = async (enquiryId, customerName) => {
    if (window.confirm(`Are you sure you want to delete the enquiry from ${customerName}?`)) {
      try {
        await deleteDoc(doc(db, 'productEnquiries', enquiryId));
        setEnquiries(enquiries.filter(enquiry => enquiry.id !== enquiryId));
        alert('Enquiry deleted successfully!');
      } catch (error) {
        console.error('Error deleting enquiry:', error);
        alert('Error deleting enquiry');
      }
    }
  };

  const filteredEnquiries = filterStatus === 'all' 
    ? enquiries 
    : enquiries.filter(enquiry => enquiry.status === filterStatus);

  const getStatusColor = (status) => {
    const statusConfig = statusOptions.find(option => option.value === status);
    return statusConfig ? statusConfig.color : 'gray';
  };

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString() + ' ' + dateObj.toLocaleTimeString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Product Enquiries</h2>
          <p className="text-gray-600">Manage customer product enquiries and requests</p>
        </div>
        <div className="bg-blue-50 px-4 py-2 rounded-lg">
          <span className="text-blue-800 font-semibold">Total: {enquiries.length}</span>
        </div>
      </div>

      {/* Status Filter */}
      <div className="bg-white rounded-lg shadow-md border p-4">
        <h3 className="text-lg font-semibold mb-3">Filter by Status</h3>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map((status) => (
            <button
              key={status.value}
              onClick={() => setFilterStatus(status.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterStatus === status.value
                  ? `bg-${status.color}-600 text-white`
                  : `bg-${status.color}-100 text-${status.color}-800 hover:bg-${status.color}-200`
              }`}
            >
              {status.label}
              {status.value !== 'all' && (
                <span className="ml-2 bg-white bg-opacity-30 px-2 py-1 rounded text-xs">
                  {enquiries.filter(e => e.status === status.value).length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Enquiries List */}
      <div className="bg-white rounded-lg shadow-md border">
        {filteredEnquiries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <div className="text-4xl mb-4">📝</div>
            <h3 className="text-lg font-medium mb-2">No enquiries found</h3>
            <p>No product enquiries match the current filter.</p>
          </div>
        ) : (
          <div className="divide-y">
            {filteredEnquiries.map((enquiry) => (
              <div key={enquiry.id} className="p-6 hover:bg-gray-50">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-burlap-100 to-burlap-200 rounded-lg overflow-hidden border">
                      <img 
                        src={enquiry.productImage} 
                        alt={enquiry.productName}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full bg-gradient-to-br from-burlap-100 to-burlap-200 flex items-center justify-center hidden">
                        <span className="text-lg text-burlap-400">🎁</span>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{enquiry.customerName}</h4>
                      <p className="text-sm text-gray-600">{enquiry.customerEmail}</p>
                      <p className="text-sm text-gray-600">{enquiry.customerPhone}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium bg-${getStatusColor(enquiry.status)}-100 text-${getStatusColor(enquiry.status)}-800`}>
                      {enquiry.status?.charAt(0).toUpperCase() + enquiry.status?.slice(1) || 'New'}
                    </span>
                    <button
                      onClick={() => setSelectedEnquiry(enquiry)}
                      className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                    >
                      View Details
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Product: </span>
                    <span className="text-gray-600">{enquiry.productName}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Category: </span>
                    <span className="text-gray-600 capitalize">{enquiry.productCategoryName || enquiry.productCategory}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Quantity: </span>
                    <span className="text-gray-600">{enquiry.quantity || 1}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Date: </span>
                    <span className="text-gray-600">{formatDate(enquiry.enquiryDate)}</span>
                  </div>
                </div>

                {enquiry.message && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium text-gray-700">Message: </span>
                    <span className="text-gray-600">{enquiry.message}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Detailed Enquiry Modal */}
      {selectedEnquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary-500 to-accent-500 text-white p-6 rounded-t-2xl">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Enquiry Details</h2>
                <button
                  onClick={() => setSelectedEnquiry(null)}
                  className="text-white hover:text-gray-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Product & Customer Info */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Product Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Product Details</h3>
                  <div className="w-full h-48 bg-gradient-to-br from-burlap-100 to-burlap-200 rounded-lg overflow-hidden border">
                    <img 
                      src={selectedEnquiry.productImage} 
                      alt={selectedEnquiry.productName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="space-y-2">
                    <p><span className="font-medium">Name:</span> {selectedEnquiry.productName}</p>
                    <p><span className="font-medium">Category:</span> {selectedEnquiry.productCategoryName || selectedEnquiry.productCategory}</p>
                    <p><span className="font-medium">Price:</span> ₹{selectedEnquiry.productPrice}</p>
                    <p><span className="font-medium">Description:</span> {selectedEnquiry.productDescription}</p>
                  </div>
                </div>

                {/* Customer Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800 border-b pb-2">Customer Details</h3>
                  <div className="space-y-3">
                    <p><span className="font-medium">Name:</span> {selectedEnquiry.customerName}</p>
                    <p><span className="font-medium">Email:</span> {selectedEnquiry.customerEmail}</p>
                    <p><span className="font-medium">Phone:</span> {selectedEnquiry.customerPhone}</p>
                    <p><span className="font-medium">Quantity:</span> {selectedEnquiry.quantity || 1}</p>
                    <p><span className="font-medium">Enquiry Date:</span> {formatDate(selectedEnquiry.enquiryDate)}</p>
                    
                    {selectedEnquiry.message && (
                      <div>
                        <span className="font-medium">Message:</span>
                        <p className="mt-1 p-3 bg-gray-50 rounded-lg text-gray-700">
                          {selectedEnquiry.message}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Status Management */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Update Status</h3>
                <div className="flex flex-wrap gap-2">
                  {statusOptions.filter(s => s.value !== 'all').map((status) => (
                    <button
                      key={status.value}
                      onClick={() => updateEnquiryStatus(selectedEnquiry.id, status.value)}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        selectedEnquiry.status === status.value
                          ? `bg-${status.color}-600 text-white`
                          : `bg-${status.color}-100 text-${status.color}-800 hover:bg-${status.color}-200`
                      }`}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t">
                <button
                  onClick={() => deleteEnquiry(selectedEnquiry.id, selectedEnquiry.customerName)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete Enquiry
                </button>
                <button
                  onClick={() => setSelectedEnquiry(null)}
                  className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductEnquiriesSection;
